// src/components/HLVForm.js
import React, { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import axios from "axios";
import HLVInfo from "./HLVInfo";

const HLVForm = ({ setUserInfo, userInfo }) => {
  const {
    setValue,
    control,
    register,
    handleSubmit,
    formState: { errors, values },
  } = useForm({
    defaultValues: userInfo,
  });

  useEffect(() => {
    setValue("monthlyLiabilities", userInfo?.monthlyLiabilities || 0);
    setValue("retirementAge", userInfo?.retirementAge || 60);
  }, [userInfo, setValue]);

  const saveToDatabase = async (data) => {
    console.log("data: ", data);
    // Implement your logic to save the data to the database/API

    const filteredData = Object.fromEntries(
      Object.entries(data).filter(([_, value]) => value !== ""),
    );

    console.log("filteredData: ", filteredData);
    const _saving =
      Number(filteredData?.monthlyIncome) -
      (Number(filteredData?.monthlyExpenses ?? 0) +
        Number(filteredData?.monthlyLiabilities ?? 0));

    const _userInfo = {
      ...filteredData,
      editing: false, // saving to parentState with editing false
      monthlySavings: isNaN(_saving) ? 0 : _saving,
    };

    let domain = "";
    if (window.location.hostname === "localhost") {
      domain = process.env.REACT_APP_LOCAL_EXPRESS;
    } else if (window.location.hostname.trim() === "it.acuvisor.com") {
      domain = process.env.REACT_APP_PRODUCTION_EXPRESS;
    } else {
      domain = process.env.REACT_APP_UAT_EXPRESS;
    }

    try {
      const response = await axios.post(
        `${domain}/api/customer/quick-hlv-user`,
        _userInfo,
      );
      console.log("Response while saving to db", response);
      const _userId = response?.data?.id;
      setUserInfo({ ..._userInfo, id: _userId });

      // Todo: for now calling send-email from here but it can be moved as required
      const emailResponse = await axios.post(
        `${domain}/api/customer/quick-hlv-user/send-email/${_userId}`,
      );
      console.log("Response while sending mail:", emailResponse);
    } catch (error) {
      const _err_msg = error?.response;
      console.error("Error while saving to db", error, _err_msg);
      alert(_err_msg?.data?.message ?? "Something went Wrong !");
    }

    console.log("Saving to the database:", _userInfo);
  };

  const watchChildren = useWatch({ name: "children", control: control });
  const watchSmoke = useWatch({ name: "smoking", control: control });
  const watchDrink = useWatch({ name: "drinking", control: control });
  const watchIncome = useWatch({ name: "monthlyIncome", control: control });
  const watchExpenses = useWatch({ name: "monthlyExpenses", control: control });
  const watchLiabilities = useWatch({
    name: "monthlyLiabilities",
    control: control,
  });

  return (
    <div className="flex">
      <div className="w-3/5 px-4">
        <HLVInfo />
      </div>
      <div className="w-2/5 bg-slate-100 p-4 rounded-md">
        <form onSubmit={handleSubmit(saveToDatabase)}>
          <div className="mb-4">
            <label className="block text-sm font-bold mb-2" htmlFor="fullName">
              FULL NAME <span className="text-red-500">*</span>
            </label>
            <input
              {...register("fullName", {
                required: true,
                minLength: { value: 3, message: "Please enter full name" },
              })}
              className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
              id="fullName"
              type="text"
              placeholder="John Doe"
            />
            {errors.fullName && (
              <span className="text-red-500">Full Name is required</span>
            )}
          </div>

          <div className="mb-4">
            <label
              className="block text-sm font-bold mb-2"
              htmlFor="mobileNumber"
            >
              MOBILE NUMBER <span className="text-red-500">*</span>
            </label>
            <input
              {...register("mobileNumber", {
                required: "Mobile number is required",
                pattern: {
                  value: /^[0-9]{10}$/,
                  message: "Invalid mobile number",
                },
              })}
              className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
              id="mobileNumber"
              type="tel"
              placeholder="1234567890"
              onInput={(e) => {
                if (10 && e.target.value.length > 10) {
                  e.target.value = e.target.value.slice(0, 10);
                }
              }}
            />
            {errors.mobileNumber && (
              <span className="text-red-500">
                {errors.mobileNumber.message}
              </span>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-bold mb-2" htmlFor="email">
              EMAIL <span className="text-red-500">*</span>
            </label>
            <input
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                  message: "Invalid email address",
                },
              })}
              className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
              id="email"
              type="email"
              placeholder="john.doe@example.com"
            />
            {errors.email && (
              <span className="text-red-500">{errors.email.message}</span>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-bold mb-2" htmlFor="gender">
              GENDER <span className="text-red-500">*</span>
            </label>
            <select
              {...register("gender", { required: "Please select your gender" })}
              className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
              id="gender"
            >
              <option value="">Select</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
            {errors.gender && (
              <span className="text-red-500">{errors.gender.message}</span>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-bold mb-2" htmlFor="dob">
              DOB—CALCULATE CURRENT AGE <span className="text-red-500">*</span>
            </label>
            <input
              {...register("dob", {
                required: "Date of birth is required",
                max: {
                  value: new Date().toISOString().split("T")[0], // set max date to today
                  message: "Invalid DOB",
                },
              })}
              className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
              id="dob"
              type="date"
            />
            {errors.dob && (
              <span className="text-red-500">{errors.dob.message}</span>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-bold mb-2" htmlFor="married">
              MARRIED
            </label>
            <select
              {...register("married")}
              className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
              id="married"
            >
              <option value="">Select</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-bold mb-2" htmlFor="children">
              CHILDREN - Y/N, IF YES, AGE OF CHILDREN
            </label>
            <div className="flex">
              <select
                {...register("children")}
                className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline mr-2"
                id="children"
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
              {watchChildren === "yes" && (
                <input
                  {...register("childAge", {
                    required: "Please enter the child's age",
                  })}
                  className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
                  id="childAge"
                  type="text"
                  placeholder="Enter eldest child age"
                />
              )}
            </div>
            {errors.childAge && (
              <span className="text-red-500">{errors.childAge.message}</span>
            )}
          </div>

          <div className="mb-4">
            <label
              className="block text-sm font-bold mb-2"
              htmlFor="retirementAge"
            >
              RETIREMENT AGE <span className="text-red-500">*</span>
            </label>
            <input
              {...register("retirementAge", {
                required: "Retirement age is required",
              })}
              className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
              id="retirementAge"
              type="number"
              placeholder="Enter retirement age"
              onWheel={(e) => e.currentTarget.blur()}
            />
            {errors.retirementAge && (
              <span className="text-red-500">
                {errors.retirementAge.message}
              </span>
            )}
          </div>
          <div className="mb-4">
            <label
              className="block text-sm font-bold mb-2"
              htmlFor="monthlyIncome"
            >
              MONTHLY INCOME <span className="text-red-500">*</span>
            </label>
            <input
              {...register("monthlyIncome", {
                required: "Monthly income is required",
              })}
              className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
              id="monthlyIncome"
              type="number"
              placeholder="Enter monthly income"
              onWheel={(e) => e.currentTarget.blur()}
            />
            {errors.monthlyIncome && (
              <span className="text-red-500">
                {errors.monthlyIncome.message}
              </span>
            )}
          </div>

          <div className="mb-4">
            <label
              className="block text-sm font-bold mb-2"
              htmlFor="monthlyExpenses"
            >
              MONTHLY EXPENSES <span className="text-red-500">*</span>
            </label>
            <input
              {...register("monthlyExpenses", {
                required: "Monthly expenses are required",
                max: {
                  value: watchIncome,
                  message: "Monthly expenses cannot exceed monthly income",
                },
              })}
              className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
              id="monthlyExpenses"
              type="number"
              placeholder="Enter monthly expenses"
              onWheel={(e) => e.currentTarget.blur()}
            />
            {errors.monthlyExpenses && (
              <span className="text-red-500">
                {errors.monthlyExpenses.message}
              </span>
            )}
          </div>

          <div className="mb-4">
            <label
              className="block text-sm font-bold mb-2"
              htmlFor="monthlyInvestment"
            >
              Monthly Investment <span className="text-red-500">*</span>
            </label>
            <input
              {...register("monthlyInvestment", {
                required: "Monthly Investment are required",
              })}
              className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
              id="monthlyInvestment"
              type="number"
              placeholder="Enter monthly Investment"
              onWheel={(e) => e.currentTarget.blur()}
            />
            {errors.monthlyInvestment && (
              <span className="text-red-500">
                {errors.monthlyInvestment.message}
              </span>
            )}
          </div>
          <div className="mb-4">
            <label
              className="block text-sm font-bold mb-2"
              htmlFor="monthlyLiabilities"
            >
              MONTHLY LIABILITIES (HOME LOAN EMI AND OTHER LOANS){" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              {...register("monthlyLiabilities", {
                required: "Monthly liabilities are required",
                max: {
                  value: watchIncome,
                  message: "Monthly expenses cannot exceed monthly income",
                },
              })}
              className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
              id="monthlyLiabilities"
              type="number"
              placeholder="Enter monthly liabilities"
              onWheel={(e) => e.currentTarget.blur()}
            />
            {errors.monthlyLiabilities && (
              <span className="text-red-500">
                {errors.monthlyLiabilities.message}
              </span>
            )}
          </div>

          <div className="mb-4">
            <label
              className="block text-sm font-bold mb-2"
              htmlFor="monthlySavings"
            >
              MONTHLY SAVINGS <span className="text-red-500">*</span>
            </label>
            <input
              {...register("monthlySavings", {
                required: "Monthly Savings are required",
              })}
              className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
              id="monthlySavings"
              type="number"
              onWheel={(e) => e.currentTarget.blur()}
              placeholder="Enter monthly savings"
              name="monthlySavings"
              value={
                Number(watchIncome ?? 0) -
                (Number(watchExpenses ?? 0) + Number(watchLiabilities ?? 0))
              }
              readOnly={true}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-bold mb-2" htmlFor="address">
              CURRENT/PERMANENT ADDRESS
            </label>
            <textarea
              {...register("address")}
              className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
              id="address"
              placeholder="Enter address"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-bold mb-2" htmlFor="pincode">
              PINCODE
            </label>
            <input
              {...register("pincode", {
                required: "Pincode is required",
                min: { value: 100000, message: "Enter a valid pincode" },
                max: { value: 999999, message: "Enter a valid pincode" },
              })}
              type="number"
              className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
              id="pincode"
              placeholder="Enter pincode"
              onInput={(e) => {
                if (6 && e.target.value.length > 6) {
                  e.target.value = e.target.value.slice(0, 6);
                }
              }}
              onWheel={(e) => e.currentTarget.blur()}
            />
            {errors.pincode && (
              <span className="text-red-500">{errors.pincode.message}</span>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-bold mb-2" htmlFor="lifeCover">
              EXISTING LIFE COVER (SUM ASSURED) AND TOTAL PREMIUM PAID
            </label>
            <div className="flex">
              <input
                {...register("lifeCoverSumAssured")}
                className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline mr-2"
                id="lifeCoverSumAssured"
                type="text"
                placeholder="Sum assured"
              />
              <input
                {...register("lifeCoverTotalPremiumPaid")}
                className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
                id="lifeCoverTotalPremiumPaid"
                type="text"
                placeholder="Total premium paid"
              />
            </div>
          </div>

          <div className="mb-4">
            <label
              className="block text-sm font-bold mb-2"
              htmlFor="healthCover"
            >
              EXISTING HEALTH COVER (SUM INSURED) AND TOTAL PREMIUM PAID
            </label>
            <div className="flex">
              <input
                {...register("healthCoverSumInsured")}
                className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline mr-2"
                id="healthCoverSumInsured"
                type="text"
                placeholder="Sum insured"
              />
              <input
                {...register("healthCoverTotalPremiumPaid")}
                className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
                id="healthCoverTotalPremiumPaid"
                type="text"
                placeholder="Total premium paid"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-bold mb-2" htmlFor="smoking">
              SMOKING
            </label>
            <select
              {...register("smoking")}
              className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
              id="smoking"
            >
              <option value="">Select</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            {watchSmoke === "yes" && (
              <textarea
                {...register("smokingDetails")}
                className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline mt-2"
                id="smokingDetails"
                placeholder="Enter smoking details"
              />
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-bold mb-2" htmlFor="drinking">
              DRINKING
            </label>
            <select
              {...register("drinking")}
              className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
              id="drinking"
            >
              <option value="">Select</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            {watchDrink === "yes" && (
              <textarea
                {...register("drinkingDetails")}
                className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline mt-2"
                id="drinkingDetails"
                placeholder="Enter drinking details"
              />
            )}
          </div>

          <div className="mb-4">
            <label
              className="block text-sm font-bold mb-2"
              htmlFor="twoWheeler"
            >
              TWO-WHEELER
            </label>
            <select
              {...register("twoWheeler")}
              className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
              id="twoWheeler"
            >
              <option value="">Select</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div className="mb-4">
            <label
              className="block text-sm font-bold mb-2"
              htmlFor="fourWheeler"
            >
              FOUR-WHEELER
            </label>
            <select
              {...register("fourWheeler")}
              className="appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
              id="fourWheeler"
            >
              <option value="">Select</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            type="submit"
          >
            Submit
          </button>
        </form>
      </div>
    </div>
  );
};

export default HLVForm;
